import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getEdges, registerEdge } from "../api/edgeApi";
import { getTenants } from "../api/tenantApi";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";


export default function EdgeList() {
  const navigate = useNavigate();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const isSuperAdmin = me?.global_role === "superadmin";

  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [createdEdgeId, setCreatedEdgeId] = useState("");

  // Form states
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [edgeId, setEdgeId] = useState("");
  const [model, setModel] = useState("");
  const [firmware, setFirmware] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!accessToken) return;

    async function fetchEdges() {
      try {
        setLoading(true);
        const data = await getEdges(accessToken);
        setEdges(data);
      } catch (err) {
        setError(err.response?.data?.detail || "Edge listesi alınamadı");
      } finally {
        setLoading(false);
      }
    }

    fetchEdges();
  }, [accessToken]);


  // Fetch tenants (for superadmin)
  useEffect(() => {
    if (!isSuperAdmin || !accessToken) return;

    async function fetchTenants() {
      try {
        const data = await getTenants(accessToken, { limit: 100 });
        setTenants(data.items || []);
      } catch (err) {
        console.error("Tenant listesi alınamadı:", err);
      }
    }

    fetchTenants();
  }, [isSuperAdmin, accessToken]);

  // Open create modal
  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
    setSelectedTenantId("");
    setEdgeId("");
    setModel("");
    setFirmware("");
  };

  // Close create modal
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  const handleSubmitCreate = async () => {
    if (!selectedTenantId || !edgeId.trim()) {
      toast.error("Tenant ve Edge ID zorunludur");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        edgeId: edgeId.trim(),
        location_id: null,
        device: {
          manufacturer: "",
          model: model.trim() || "",
          firmware: firmware.trim() || "",
        },
        asset: {
          assetId: "",
          type: "",
          location: "",
        },
      };

      const result = await registerEdge(accessToken, selectedTenantId, payload);

      setCreatedEdgeId(result.id); // DB'deki UUID
      setCreateModalOpen(false);
      setSuccessModalOpen(true);


      const data = await getEdges(accessToken);
      setEdges(data);
    } catch (err) {

      let errorMessage = "Cihaz oluşturulamadı";

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;

        if (detail.includes("already exists")) {
          errorMessage = "Bu Edge ID zaten kullanımda. Lütfen farklı bir ID girin.";
        } else if (detail.includes("not found")) {
          errorMessage = "Tenant bulunamadı.";
        } else if (detail.includes("not allowed")) {
          errorMessage = "Bu işlem için yetkiniz yok.";
        } else if (typeof detail === "string") {
          errorMessage = detail;
        }
      }

      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Copy ID to clipboard
  const handleCopyId = () => {
    navigator.clipboard.writeText(createdEdgeId);
    toast.success("ID kopyalandı!");
  };

  // Close success modal
  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    setCreatedEdgeId("");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ color: "#f85149", textAlign: "center", mt: 4 }}>
        <Typography>Hata: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ color: "#e6edf3" }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#e6edf3" }}>
            Edge Cihazları
          </Typography>
          <Typography variant="body2" sx={{ color: "#8b949e", mt: 0.5 }}>
            {isSuperAdmin ? "Tüm edge cihazları" : "Size atanmış edge cihazları"}
          </Typography>
        </div>

        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateModal}
            sx={{
              backgroundColor: "#238636",
              color: "#fff",
              "&:hover": { backgroundColor: "#2ea043" },
            }}
          >
            Cihaz Ekle
          </Button>
        )}
      </Box>

      {edges.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "#8b949e" }}>
          <Typography>Henüz edge cihazı bulunmuyor.</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "#161b22",
            border: "1px solid #30363d",
            borderRadius: "8px",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#21262d" }}>
                <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Edge ID</TableCell>
                {isSuperAdmin && (
                  <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Tenant</TableCell>
                )}
                <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Lokasyon</TableCell>
                <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Asset ID</TableCell>
                <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Hat Adı</TableCell>
                <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Model</TableCell>
                <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Firmware</TableCell>
                <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Kayıt Tarihi</TableCell>
                <TableCell sx={{ color: "#8b949e", fontWeight: 600 }} align="center">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {edges.map((edge) => (
                <TableRow
                  key={edge.id}
                  sx={{
                    backgroundColor: "#161b22",
                    "&:hover": { backgroundColor: "#21262d" },
                  }}
                >
                  <TableCell sx={{ color: "#e6edf3", fontWeight: 500 }}>{edge.edgeId}</TableCell>
                  {isSuperAdmin && (
                    <TableCell sx={{ color: "#e6edf3" }}>
                      {edge.tenant_name || edge.tenantName || "-"}
                    </TableCell>
                  )}
                  <TableCell sx={{ color: "#e6edf3" }}>{edge.locationName || "-"}</TableCell>
                  <TableCell sx={{ color: "#e6edf3" }}>{edge.asset?.assetId || "-"}</TableCell>
                  <TableCell sx={{ color: "#e6edf3" }}>{edge.asset?.location || "-"}</TableCell>
                  <TableCell sx={{ color: "#8b949e" }}>{edge.device?.model || "-"}</TableCell>
                  <TableCell sx={{ color: "#8b949e" }}>{edge.device?.firmware || "-"}</TableCell>
                  <TableCell sx={{ color: "#8b949e" }}>
                    {edge.created_at ? new Date(edge.created_at).toLocaleDateString("tr-TR") : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      sx={{ color: "#238636", mr: 0.5 }}
                      title="Dashboard'a Git"
                      onClick={() => navigate("/home?edgeId=" + edge.id)}
                    >
                      <DashboardIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: "#58a6ff" }}
                      title="Detay"
                      onClick={() => navigate(`/edges/${edge.id}`)}
                    >
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* CREATE MODAL */}
      <Dialog
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#0d1117",
            color: "#e6edf3",
            border: "1px solid #30363d",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #30363d" }}>
          Edge Cihaz Ekle
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: "#8b949e", mb: 3 }}>
            Cihaz Bilgileri
          </Typography>

          {/* Tenant Dropdown */}
          <TextField
            select
            fullWidth
            label="Tenant *"
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            sx={{
              mb: 2,
              "& .MuiInputBase-root": { color: "#e6edf3" },
              "& .MuiInputLabel-root": { color: "#8b949e" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#30363d" },
                "&:hover fieldset": { borderColor: "#58a6ff" },
              },
            }}
          >
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Edge ID */}
          <TextField
            fullWidth
            label="Edge ID *"
            value={edgeId}
            onChange={(e) => setEdgeId(e.target.value)}
            sx={{
              mb: 2,
              "& .MuiInputBase-root": { color: "#e6edf3" },
              "& .MuiInputLabel-root": { color: "#8b949e" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#30363d" },
                "&:hover fieldset": { borderColor: "#58a6ff" },
              },
            }}
          />

          {/* Model and Firmware */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              fullWidth
              label="Model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              sx={{
                "& .MuiInputBase-root": { color: "#e6edf3" },
                "& .MuiInputLabel-root": { color: "#8b949e" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#30363d" },
                  "&:hover fieldset": { borderColor: "#58a6ff" },
                },
              }}
            />
            <TextField
              fullWidth
              label="Firmware"
              value={firmware}
              onChange={(e) => setFirmware(e.target.value)}
              sx={{
                "& .MuiInputBase-root": { color: "#e6edf3" },
                "& .MuiInputLabel-root": { color: "#8b949e" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#30363d" },
                  "&:hover fieldset": { borderColor: "#58a6ff" },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #30363d", p: 2 }}>
          <Button onClick={handleCloseCreateModal} sx={{ color: "#8b949e" }}>
            İptal
          </Button>
          <Button
            onClick={handleSubmitCreate}
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: "#238636",
              "&:hover": { backgroundColor: "#2ea043" },
            }}
          >
            {submitting ? "Oluşturuluyor..." : "Oluştur"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SUCCESS MODAL - ID GÖSTERME */}
      <Dialog
        open={successModalOpen}
        onClose={handleCloseSuccessModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#0d1117",
            color: "#e6edf3",
            border: "1px solid #30363d",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #30363d", color: "#3fb950" }}>
          ✅ Cihaz Başarıyla Oluşturuldu!
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Alert severity="success" sx={{ mb: 3, backgroundColor: "#0d1117", color: "#3fb950" }}>
            Edge cihazı veritabanına kaydedildi.
          </Alert>

          <Typography variant="body2" sx={{ color: "#8b949e", mb: 1 }}>
            Cihaz ID:
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 2,
              backgroundColor: "#161b22",
              border: "1px solid #30363d",
              borderRadius: "6px",
            }}
          >
            <Typography
              sx={{
                flex: 1,
                fontFamily: "monospace",
                fontSize: "16px",
                color: "#58a6ff",
                fontWeight: "bold",
              }}
            >
              {createdEdgeId}
            </Typography>
            <IconButton
              size="small"
              onClick={handleCopyId}
              sx={{ color: "#8b949e", "&:hover": { color: "#e6edf3" } }}
              title="ID'yi Kopyala"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" sx={{ color: "#8b949e", mt: 2 }}>
            Bu ID'yi fiziksel cihazın kurulum ekranına girin.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #30363d", p: 2 }}>
          <Button
            onClick={handleCloseSuccessModal}
            variant="contained"
            sx={{
              backgroundColor: "#238636",
              "&:hover": { backgroundColor: "#2ea043" },
            }}
          >
            Tamam
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}